import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { BsPlusCircleFill } from 'react-icons/bs';
import { CiEdit } from 'react-icons/ci';
import { RiDeleteBin5Line, RiGalleryUploadLine } from 'react-icons/ri';
import { FaStar } from 'react-icons/fa';

interface TestimonialItem {
  id: number;
  name: string;
  position: string;
  content: string;
  image: string;
  review: number;
}

const Testimonials = () => {
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [sliderdata, setSliderdata] = useState<TestimonialItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get<TestimonialItem[]>(
        "https://rmbbackend.rmbjaipur.com/auth/testimonial"
      );
      setSliderdata(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Testimonial fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openModal = (postId: string) => {
    setSelectedPostId(postId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedPostId(null);
    setIsModalOpen(false);
    fetchData();
  };

  const deletePost = async (id: number) => {
    if (!id) return;
    const confirmation = window.confirm('Are you sure you want to delete this testimonial?');
    if (!confirmation) return;

    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/testimonial/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      alert('Failed to delete testimonial. Please try again.');
    }
  };

  return (
    <div className='p-6 bg-slate-100 min-h-screen'>
      <div className='max-w-7xl mx-auto'>
        {/* Header bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
          <div>
            <h1 className="text-2xl font-black text-[#0A2451]">International Co-Chair Testimonials</h1>
            <p className="text-xs text-slate-500 mt-1">Manage Co-Chair and Leadership testimonials displayed on the main home page.</p>
          </div>
          <button
            onClick={() => openModal('')}
            className='flex items-center gap-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black px-5 py-2.5 rounded-xl shadow transition cursor-pointer text-xs'
          >
            <BsPlusCircleFill size={16} /> Add New Testimonial
          </button>
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 animate-pulse space-y-4">
                <div className="h-44 bg-slate-200 rounded-xl" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
                <div className="h-3 bg-slate-100 rounded w-full" />
              </div>
            ))}
          </div>
        ) : sliderdata.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sliderdata.map((item) => (
              <div key={item.id} className='bg-white rounded-2xl p-5 shadow-sm border border-slate-200 hover:border-[#FDBC12] transition flex flex-col justify-between group'>
                <div>
                  <div className="relative h-48 w-full rounded-xl overflow-hidden mb-4 bg-slate-900 border border-slate-100">
                    <img
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                      src={
                        item.image
                          ? (item.image.startsWith("http")
                              ? item.image
                              : `https://rmbbackend.rmbjaipur.com/${item.image.replace(/\\/g, '/')}`)
                          : "https://via.placeholder.com/300x200?text=No+Image"
                      }
                      alt={item.name || "Testimonial"}
                      onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/300x200?text=No+Image"; }}
                    />
                    <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full text-amber-400 text-xs font-extrabold flex items-center gap-1">
                      <FaStar /> {item.review || 5}/5
                    </div>
                  </div>

                  <h3 className='font-bold text-slate-900 text-lg leading-tight'>{item.name}</h3>
                  <p className="text-xs font-semibold text-[#FDBC12] mt-0.5 mb-2">{item.position || 'Co-Chair / Member'}</p>
                  <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed">
                    "{item.content}"
                  </p>
                </div>

                <div className='flex items-center justify-between gap-3 border-t border-slate-100 pt-4 mt-4'>
                  <button
                    onClick={() => openModal(item.id.toString())}
                    className='flex-1 flex justify-center items-center gap-1.5 py-2 border border-[#0A2451] text-[#0A2451] hover:bg-[#0A2451] hover:text-white rounded-xl text-xs font-bold transition cursor-pointer'
                  >
                    <CiEdit size={16} /> Edit
                  </button>
                  <button
                    onClick={() => deletePost(item.id)}
                    className='flex-1 flex justify-center items-center gap-1.5 py-2 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white rounded-xl text-xs font-bold transition cursor-pointer'
                  >
                    <RiDeleteBin5Line size={16} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 text-slate-400">
            No testimonials found in database.
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <Modal postId={selectedPostId} onClose={closeModal} />
      )}
    </div>
  );
};

interface ModalProps {
  postId?: string | null;
  onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({ postId, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    review: 5,
    content: '',
    image: null as File | null,
  });
  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (postId) {
      const fetchPost = async () => {
        try {
          const response = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/testimonial/${postId}`);
          const data = Array.isArray(response.data) ? response.data[0] : response.data;
          if (data) {
            setFormData({
              name: data.name || '',
              position: data.position || '',
              review: data.review || 5,
              content: data.content || '',
              image: null,
            });
            setExistingImage(data.image || null);
            setFileName(data.image || null);
          }
        } catch (err) {
          console.error('Error fetching testimonial:', err);
        }
      };
      fetchPost();
    }
  }, [postId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, image: file }));
    setFileName(file ? file.name : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.content.trim()) {
      setError('Name and description content are required.');
      return;
    }

    setSubmitting(true);
    setError('');

    const data = new FormData();
    data.append('name', formData.name);
    data.append('position', formData.position);
    data.append('review', formData.review.toString());
    data.append('content', formData.content);

    if (formData.image) {
      data.append('image', formData.image);
    } else if (existingImage) {
      data.append('image', existingImage);
    }

    try {
      if (postId) {
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/testimonial/${postId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post('https://rmbbackend.rmbjaipur.com/auth/testimonial', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onClose();
    } catch (err) {
      console.error('Error submitting testimonial:', err);
      setError('Failed to save testimonial. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg p-6 rounded-2xl shadow-2xl relative border border-slate-100 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-black text-[#0A2451] mb-4 pb-2 border-b border-slate-100">
          {postId ? 'Edit Testimonial' : 'Add New Testimonial'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Rajamohan Dhandapani"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-[#FDBC12]"
              required
            />
          </div>

          {/* Position */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Designation / Role</label>
            <input
              type="text"
              name="position"
              value={formData.position}
              onChange={handleChange}
              placeholder="e.g. Chair - RMB International"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-[#FDBC12]"
              required
            />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Rating Review (1–5 Stars)</label>
            <select
              name="review"
              value={formData.review}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-[#FDBC12]"
              required
            >
              <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
              <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
              <option value="3">⭐⭐⭐ (3 Stars)</option>
              <option value="2">⭐⭐ (2 Stars)</option>
              <option value="1">⭐ (1 Star)</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Testimonial Content</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Write the full testimonial message..."
              rows={4}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm font-medium text-slate-900 outline-none focus:border-[#FDBC12]"
              required
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Profile Photo / Media</label>
            <div
              onClick={() => document.getElementById('file-upload-input')?.click()}
              className="w-full border-2 border-dashed border-slate-300 hover:border-[#FDBC12] bg-slate-50 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer transition"
            >
              <RiGalleryUploadLine size={28} className="text-slate-400 mb-1" />
              <input
                id="file-upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              {fileName ? (
                <p className="text-xs text-[#0A2451] font-bold text-center truncate max-w-full">
                  Selected: <span className="text-[#FDBC12]">{fileName}</span>
                </p>
              ) : (
                <p className="text-xs text-slate-500 font-semibold text-center">
                  Click or drag photo to upload
                </p>
              )}
            </div>
          </div>

          {error && <p className="text-xs text-red-600 font-bold">{error}</p>}

          {/* Action buttons */}
          <div className='flex justify-end items-center gap-3 pt-3 border-t border-slate-100'>
            <button
              type="button"
              onClick={onClose}
              className='px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-bold transition'
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className='px-6 py-2.5 rounded-xl bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] text-xs font-black transition cursor-pointer shadow'
            >
              {submitting ? 'Saving...' : (postId ? 'Update Testimonial' : 'Create Testimonial')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Testimonials;